import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Carousel, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { FaArrowRight, FaCreditCard } from 'react-icons/fa';
import './Landing.css';
import ConversionProofBlock from './ConversionProofBlock';
import EncajaStarBanner from './EncajaStarBanner';
import ProductIcon from './ProductIcon';
import ProductPriceBlock from './ProductPriceBlock';
import SiteHeader from './SiteHeader';
import SaleProductCard from './SaleProductCard';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import { sendEvent } from '../utils/analytics';
import { getProductSalePricing } from '../utils/productPricing';
import {
  DEFAULT_LANDING_SECTIONS,
  getVisibleMarketingNavItems,
  mergeLandingSections
} from '../utils/landingSections';
import { ENCAJA_LOGIN_URL, ENCAJA_PLANS_ANCHOR } from '../utils/encajaBrand';
import { BRAND_LOGO_MONO } from '../utils/brandAssets';
import { countryCodeToFlagEmoji } from '../utils/countryFlags';

const API_BASE = process.env.REACT_APP_API_URL || '';

function sanitizePhone(raw) {
  return String(raw || '').replace(/[^\d]/g, '');
}

function formatLocalPhone(raw) {
  return String(raw || '').replace(/[^\d+\s()-]/g, '').trim();
}

function domainStatusKey(status) {
  const map = {
    available: 'landing.domainStatusAvailable',
    prereg: 'landing.domainStatusPrereg',
    reserved: 'landing.domainStatusReserved',
    taken: 'landing.domainStatusTaken',
    error: 'landing.domainStatusError',
    suspended: 'landing.domainStatusSuspended',
    blocked: 'landing.domainStatusBlocked',
    unknown: 'landing.domainStatusUnknown'
  };
  return map[status] || 'landing.domainStatusUnknown';
}

function formatRegistrationPrice(item, language) {
  if (item.registrationPrice != null && !Number.isNaN(Number(item.registrationPrice))) {
    const cur = item.registrationPriceCurrency || 'USD';
    const periodMonths = item.registrationPeriodMonths != null ? Number(item.registrationPeriodMonths) : null;
    if (periodMonths === 12) {
      return tf('landing.domainPriceQuoteYear', { amount: String(item.registrationPrice), currency: cur }, language);
    }
    const mo = item.registrationPeriodMonths != null ? String(item.registrationPeriodMonths) : '—';
    return tf('landing.domainPriceQuote', { amount: String(item.registrationPrice), currency: cur, months: mo }, language);
  }
  if (item.price != null && !Number.isNaN(Number(item.price))) {
    return tf('landing.domainPriceSimple', { amount: String(item.price) }, language);
  }
  return '—';
}

function isDomainSelectable(row) {
  return row && row.canRegister === true;
}

function getPostUrl(post) {
  if (!post?.slug) return '#';
  const lang = post.language === 'en' ? 'en' : 'es';
  const base = lang === 'en' ? '/news' : '/noticias';
  const paths = {
    en: { news: 'news', tech: 'tech', business: 'business', movie: 'business', series: 'tech', music: 'news' },
    es: { news: 'actualidad', tech: 'tecnologia', business: 'negocios', movie: 'negocios', series: 'tecnologia', music: 'actualidad' }
  };
  const categoryPath = paths[lang][post.category] || paths[lang].news;
  return `${base}/${categoryPath}/${post.slug}`;
}

const FEATURED_PRODUCT_SLUG = 'web-negocio-automatizacion';
const ENCAJA_FEATURED_PLAN_SLUG = 'encaja-professional';
const CATALOG_CATEGORY_ORDER = ['encaja', 'sitios-web', 'modulos-operativos', 'mantenimiento', 'infraestructura'];

function resolveIntentVariant(search) {
  const params = new URLSearchParams(search || '');
  const directIntent = String(params.get('intent') || '').trim().toLowerCase();
  if (['domain', 'web', 'software'].includes(directIntent)) return directIntent;
  const campaign = String(params.get('utm_campaign') || '').trim().toLowerCase();
  if (campaign.includes('domain')) return 'domain';
  if (campaign.includes('software') || campaign.includes('automation')) return 'software';
  if (campaign.includes('web') || campaign.includes('landing')) return 'web';
  return 'web';
}

const Landing = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [latestPosts, setLatestPosts] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [user, setUser] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainErrorKey, setDomainErrorKey] = useState(null);
  const [domainSuggestions, setDomainSuggestions] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [siteContact, setSiteContact] = useState({
    whatsappParaguay: '',
    whatsappBolivia: '',
    email: '',
    supportNoteEs: '',
    supportNoteEn: ''
  });
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [clientTestimonials, setClientTestimonials] = useState([]);
  const [landingSections, setLandingSections] = useState(DEFAULT_LANDING_SECTIONS);
  const [leadStep, setLeadStep] = useState(1);
  const [leadErrorKey, setLeadErrorKey] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    need: 'website',
    email: '',
    company: '',
    notes: ''
  });
  const [faqOpen, setFaqOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/catalog/products?active=true`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((data) => {
        if (cancelled) return;
        setCatalogProducts(Array.isArray(data.products) ? data.products : []);
      })
      .catch(() => {
        if (!cancelled) setCatalogProducts([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/testimonials?active=true`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { testimonials: [] }))
      .then((data) => {
        if (cancelled) return;
        setClientTestimonials(Array.isArray(data.testimonials) ? data.testimonials : []);
      })
      .catch(() => {
        if (!cancelled) setClientTestimonials([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      navigate(`/user?error=${encodeURIComponent(errorParam)}`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoadingNews(true);
    const lang = language === 'en' ? 'en' : 'es';
    fetch(`${API_BASE}/api/blog/posts?language=${lang}&limit=6`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((data) => {
        if (!cancelled && Array.isArray(data.posts)) setLatestPosts(data.posts);
      })
      .catch(() => {
        if (!cancelled) setLatestPosts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingNews(false);
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        setSiteContact({
          whatsappParaguay: cfg.siteContact?.whatsappParaguay || '',
          whatsappBolivia: cfg.siteContact?.whatsappBolivia || '',
          email: cfg.siteContact?.email || '',
          supportNoteEs: cfg.siteContact?.supportNoteEs || '',
          supportNoteEn: cfg.siteContact?.supportNoteEn || ''
        });
        setLandingSections(mergeLandingSections(cfg.landingSections));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const whatsappBoliviaHref = `https://wa.me/${sanitizePhone(siteContact.whatsappBolivia)}`;
  const whatsappParaguayHref = `https://wa.me/${sanitizePhone(siteContact.whatsappParaguay)}`;
  const supportNote = language === 'en' ? siteContact.supportNoteEn : siteContact.supportNoteEs;
  const intentVariant = useMemo(() => resolveIntentVariant(window.location.search), []);
  const intentCopy = useMemo(() => {
    if (intentVariant === 'domain') {
      return {
        title: 'landing.heroTitleDomain',
        titleHighlight: 'landing.heroTitleDomainHighlight',
        subtitle: 'landing.heroSubtitleDomain',
        subtitleSupport: 'landing.heroSubtitleSupportDomain'
      };
    }
    if (intentVariant === 'software') {
      return {
        title: 'landing.heroTitleSoftware',
        titleHighlight: 'landing.heroTitleSoftwareHighlight',
        subtitle: 'landing.heroSubtitleSoftware',
        subtitleSupport: 'landing.heroSubtitleSupportSoftware'
      };
    }
    return {
      title: 'landing.heroTitle',
      titleHighlight: 'landing.heroTitleHighlight',
      subtitle: 'landing.heroSubtitle',
      subtitleSupport: null
    };
  }, [intentVariant]);

  const trackLandingEvent = (section, action, label = '') => {
    sendEvent('landing_section_event', {
      section,
      action,
      label,
      language: language === 'en' ? 'en' : 'es'
    });
  };

  const handleLeadStepOne = (e) => {
    e.preventDefault();
    if (!leadForm.name.trim()) {
      setLeadErrorKey('landing.leadFormErrorNameRequired');
      trackLandingEvent('lead_form', 'step1_error_missing_name');
      return;
    }
    setLeadErrorKey(null);
    setLeadStep(2);
    trackLandingEvent('lead_form', 'step1_continue', leadForm.need);
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadForm.name.trim()) {
      setLeadErrorKey('landing.leadFormErrorNameRequired');
      trackLandingEvent('lead_form', 'submit_error_missing_name');
      return;
    }
    const lines = [
      t('landing.leadWhatsappIntro', language),
      `${t('landing.leadFieldName', language)}: ${leadForm.name.trim()}`,
      `${t('landing.leadFieldNeed', language)}: ${t(`landing.leadNeed${leadForm.need === 'domain' ? 'Domain' : leadForm.need === 'software' ? 'Software' : 'Website'}`, language)}`
    ];
    if (leadForm.email.trim()) lines.push(`${t('landing.leadFieldEmail', language)}: ${leadForm.email.trim()}`);
    if (leadForm.company.trim()) lines.push(`${t('landing.leadFieldCompany', language)}: ${leadForm.company.trim()}`);
    if (leadForm.notes.trim()) lines.push(`${t('landing.leadFieldNotes', language)}: ${leadForm.notes.trim()}`);
    const message = encodeURIComponent(lines.join('\n'));
    window.open(`${whatsappBoliviaHref}?text=${message}`, '_blank', 'noopener,noreferrer');
    setLeadErrorKey(null);
    trackLandingEvent('lead_form', 'submit_whatsapp', leadForm.need);
  };

  useEffect(() => {
    ['hero', 'domain', 'offers', 'proof', 'contact', 'final_cta'].forEach((section) => {
      trackLandingEvent(section, 'section_view');
    });
    trackLandingEvent('hero', 'intent_variant', intentVariant);
  }, [language, intentVariant]);

  async function handleDomainSearch(e) {
    e.preventDefault();
    setDomainErrorKey(null);
    setDomainSuggestions(null);
    setSelectedDomain(null);
    const q = domainInput.trim();
    if (!q) {
      setDomainErrorKey('landing.domainSearchErrorRequired');
      trackLandingEvent('domain', 'search_required_missing');
      return;
    }
    setDomainLoading(true);
    try {
      const langParam = language === 'en' ? 'en' : 'es';
      const url = `${API_BASE}/api/domain/suggestions?name=${encodeURIComponent(q)}&lang=${langParam}`;
      const r = await fetch(url, { credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        trackLandingEvent('domain', 'search_http_error', String(r.status));
        if (r.status === 503 && data.error === 'domain_check_unconfigured') {
          setDomainErrorKey('landing.domainSearchErrorUnconfigured');
        } else if (r.status === 400 && (data.error === 'domain_invalid' || data.error === 'name_required')) {
          setDomainErrorKey(
            data.error === 'name_required' ? 'landing.domainSearchErrorRequired' : 'landing.domainSearchErrorInvalid'
          );
        } else {
          setDomainErrorKey('landing.domainSearchErrorFailed');
        }
        return;
      }
      if (data && Array.isArray(data.items)) {
        setDomainSuggestions({ baseLabel: data.baseLabel, items: data.items });
        trackLandingEvent('domain', 'search_success', String(data.items.length));
      } else {
        setDomainErrorKey('landing.domainSearchErrorFailed');
        trackLandingEvent('domain', 'search_invalid_payload', q);
      }
    } catch {
      setDomainErrorKey('landing.domainSearchErrorFailed');
      trackLandingEvent('domain', 'search_exception', q);
    } finally {
      setDomainLoading(false);
    }
  }

  const getDomainCheckoutHref = (domain) => {
    const checkoutPath = `/checkout/domain?domain=${encodeURIComponent(domain)}`;
    if (user) return checkoutPath;
    return `/user?return=${encodeURIComponent(checkoutPath)}`;
  };

  const getProductCheckoutHref = (targetSlug) => {
    const found = (catalogProducts || []).find((p) => p.slug === targetSlug);
    if (!found) return getServiceUrlBySlug(targetSlug);
    const checkoutPath = `/checkout/product?slug=${encodeURIComponent(found.slug)}`;
    if (user) return checkoutPath;
    return `/user?return=${encodeURIComponent(checkoutPath)}`;
  };

  const getServiceUrlBySlug = (targetSlug) => {
    const found = (catalogProducts || []).find((p) => p.slug === targetSlug);
    if (!found) return '/user';
    const base = language === 'en' ? '/services' : '/servicios';
    return `${base}/${found.slug}`;
  };

  const getOfferPriceLabel = (slug, fallbackKey) => {
    const found = (catalogProducts || []).find((p) => p.slug === slug);
    if (!found) return t(fallbackKey, language);
    const amount = Number(found.price);
    if (Number.isFinite(amount) && amount <= 0) return t('landing.productPriceOnRequest', language);
    const formatted = Number.isFinite(amount) ? amount.toFixed(0) : found.price;
    return `${found.currency || 'USD'} ${formatted}`;
  };

  const getProductPriceSuffix = (product) => (
    product?.category_slug === 'mantenimiento'
      || product?.category_slug === 'infraestructura'
      || product?.category_slug === 'encaja'
      ? t('landing.productPriceMonthlySuffix', language)
      : ''
  );

  const catalogGroups = useMemo(() => {
    const map = new Map();
    (catalogProducts || []).forEach((product) => {
      const catSlug = product.category_slug || 'otros';
      if (!map.has(catSlug)) {
        map.set(catSlug, { slug: catSlug, name: product.category_name || catSlug, products: [] });
      }
      map.get(catSlug).products.push(product);
    });
    const ordered = CATALOG_CATEGORY_ORDER.filter((slug) => map.has(slug)).map((slug) => map.get(slug));
    map.forEach((group, slug) => {
      if (!CATALOG_CATEGORY_ORDER.includes(slug)) ordered.push(group);
    });
    return ordered;
  }, [catalogProducts]);

  const saleProducts = useMemo(
    () => (catalogProducts || [])
      .filter((product) => getProductSalePricing(product).hasDiscount)
      .sort((a, b) => getProductSalePricing(b).discountPercent - getProductSalePricing(a).discountPercent),
    [catalogProducts]
  );

  const saleProductSlugs = useMemo(
    () => new Set(saleProducts.map((product) => product.slug)),
    [saleProducts]
  );

  const catalogGroupsWithoutSaleDuplicates = useMemo(
    () => catalogGroups
      .map((group) => ({
        ...group,
        products: group.products.filter((product) => !saleProductSlugs.has(product.slug))
      }))
      .filter((group) => group.products.length > 0),
    [catalogGroups, saleProductSlugs]
  );

  const showSection = (key) => !!landingSections[key];
  const visibleMarketingNav = useMemo(
    () => getVisibleMarketingNavItems(landingSections),
    [landingSections]
  );

  const sortedDomainItems = useMemo(() => {
    if (!domainSuggestions?.items) return [];
    return [...domainSuggestions.items].sort((a, b) => {
      const offerA = a.isSpecialOffer ? 1 : 0;
      const offerB = b.isSpecialOffer ? 1 : 0;
      if (offerA !== offerB) return offerB - offerA;
      const availableA = a.canRegister ? 1 : 0;
      const availableB = b.canRegister ? 1 : 0;
      if (availableA !== availableB) return availableB - availableA;
      return String(a.domain || '').localeCompare(String(b.domain || ''));
    });
  }, [domainSuggestions]);

  const faqItems = useMemo(
    () => [
      { q: 'landing.faqQ1', a: 'landing.faqA1' },
      { q: 'landing.faqQ2', a: 'landing.faqA2' },
      { q: 'landing.faqQ3', a: 'landing.faqA3' },
      { q: 'landing.faqQ4', a: 'landing.faqA4' },
      { q: 'landing.faqQ5', a: 'landing.faqA5' }
    ],
    []
  );

  const offerLadder = useMemo(() => ([
    {
      id: 'starter',
      slug: 'web-comercial-inicial',
      featured: false,
      badgeKey: 'landing.offerStarterBadge',
      titleKey: 'landing.offerStarterTitle',
      descKey: 'landing.offerStarterDesc',
      priceKey: 'landing.offerStarterPriceHint',
      bestForKey: 'landing.offerStarterBestFor',
      upgradeToKey: 'landing.offerStarterUpgradeTo',
      ctaKey: 'landing.offerStarterCta'
    },
    {
      id: 'growth',
      slug: 'web-negocio-automatizacion',
      featured: true,
      badgeKey: 'landing.offerBusinessBadge',
      titleKey: 'landing.offerBusinessTitle',
      descKey: 'landing.offerBusinessDesc',
      priceKey: 'landing.offerBusinessPriceHint',
      bestForKey: 'landing.offerBusinessBestFor',
      upgradeToKey: 'landing.offerBusinessUpgradeTo',
      ctaKey: 'landing.offerBusinessCta'
    },
    {
      id: 'scale',
      slug: 'escala-operativa',
      featured: false,
      badgeKey: 'landing.offerScaleBadge',
      titleKey: 'landing.offerScaleTitle',
      descKey: 'landing.offerScaleDesc',
      priceKey: 'landing.offerScalePriceHint',
      bestForKey: 'landing.offerScaleBestFor',
      upgradeToKey: 'landing.offerScaleUpgradeTo',
      ctaKey: 'landing.offerScaleCta'
    },
    {
      id: 'custom',
      slug: 'sistema-personalizado',
      featured: false,
      badgeKey: 'landing.offerCustomBadge',
      titleKey: 'landing.offerCustomTitle',
      descKey: 'landing.offerCustomDesc',
      priceKey: 'landing.offerCustomPriceHint',
      bestForKey: 'landing.offerCustomBestFor',
      upgradeToKey: 'landing.offerCustomUpgradeTo',
      ctaKey: 'landing.offerCustomCta'
    }
  ]), []);

  const serviceGroups = useMemo(() => {
    const all = [
      { id: 'domains', icon: '🌐', titleKey: 'landing.productDomainsTitle', descKey: 'landing.productDomainsDesc', slug: 'registro-dominios' },
      { id: 'hosting', icon: '☁️', titleKey: 'landing.productHostingTitle', descKey: 'landing.productHostingDesc', slug: 'hosting-plata' },
      { id: 'vps', icon: '🖥️', titleKey: 'landing.productVpsTitle', descKey: 'landing.productVpsDesc', slug: 'servidores-vps' },
      { id: 'web_simple', icon: '📄', titleKey: 'landing.productWebSimpleTitle', descKey: 'landing.productWebSimpleDesc', slug: 'web-comercial-inicial' },
      { id: 'web_custom', icon: '✨', titleKey: 'landing.productWebCustomTitle', descKey: 'landing.productWebCustomDesc', slug: 'web-negocio-automatizacion' },
      { id: 'software_custom', icon: '🤖', titleKey: 'landing.productSoftwareCustomTitle', descKey: 'landing.productSoftwareCustomDesc', slug: 'sistema-personalizado' },
      { id: 'automation', icon: '🔁', titleKey: 'landing.productAutomationTitle', descKey: 'landing.productAutomationDesc', slug: 'automatizacion-procesos' },
      { id: 'dashboards', icon: '📊', titleKey: 'landing.productDashboardsTitle', descKey: 'landing.productDashboardsDesc', slug: 'dashboards-operativos' },
      { id: 'integrations', icon: '🔌', titleKey: 'landing.productIntegrationsTitle', descKey: 'landing.productIntegrationsDesc', slug: 'integraciones-negocio' },
      { id: 'support', icon: '🛡️', titleKey: 'landing.productSupportTitle', descKey: 'landing.productSupportDesc', slug: 'soporte-sla' }
    ];
    return {
      infra: all.slice(0, 3),
      web: all.slice(3, 6),
      extended: all.slice(6)
    };
  }, []);

  const processSteps = useMemo(
    () => [
      { label: '01', titleKey: 'landing.processStep1Title', descKey: 'landing.processStep1Desc' },
      { label: '02', titleKey: 'landing.processStep2Title', descKey: 'landing.processStep2Desc' },
      { label: '03', titleKey: 'landing.processStep3Title', descKey: 'landing.processStep3Desc' },
      { label: '04', titleKey: 'landing.processStep4Title', descKey: 'landing.processStep4Desc' }
    ],
    []
  );

  const whyChooseItems = useMemo(
    () => [
      { icon: '🚀', titleKey: 'landing.whyChoose1Title', descKey: 'landing.whyChoose1Desc' },
      { icon: '📈', titleKey: 'landing.whyChoose2Title', descKey: 'landing.whyChoose2Desc' },
      { icon: '❤️', titleKey: 'landing.whyChoose3Title', descKey: 'landing.whyChoose3Desc' },
      { icon: '🪙', titleKey: 'landing.whyChoose4Title', descKey: 'landing.whyChoose4Desc' },
      { icon: '⏱️', titleKey: 'landing.whyChoose5Title', descKey: 'landing.whyChoose5Desc' },
      { icon: '🌐', titleKey: 'landing.whyChoose6Title', descKey: 'landing.whyChoose6Desc' }
    ],
    []
  );

  const getTestimonialLocalizedField = (item, base) => {
    const esValue = item[`${base}_es`];
    const enValue = item[`${base}_en`];
    if (language === 'en') return enValue || esValue || '';
    return esValue || enValue || '';
  };

  const renderTestimonialStars = (stars) => {
    const count = Math.min(5, Math.max(1, Number(stars) || 5));
    return '★'.repeat(count);
  };

  const isProductDirectCheckout = (slug) => {
    const match = catalogProducts.find((p) => p.slug === slug);
    return !!match?.direct_checkout;
  };

  const renderOfferCardActions = ({
    slug,
    directCheckout,
    featured = false,
    onPayClick,
    onDetailClick,
    payLabelKey = 'landing.productCheckoutCta',
    detailLabelKey = 'landing.productViewDetailCta',
    singleLabelKey = null
  }) => {
    if (directCheckout) {
      return (
        <div className="offer-card-actions">
          <Link
            to={getProductCheckoutHref(slug)}
            className="offer-card-cta offer-card-cta-pay"
            onClick={onPayClick}
          >
            <FaCreditCard className="offer-card-cta-icon" aria-hidden />
            <span>{t(payLabelKey, language)}</span>
          </Link>
          <Link
            to={getServiceUrlBySlug(slug)}
            className="offer-card-cta offer-card-cta-detail"
            onClick={onDetailClick}
          >
            <span>{t(detailLabelKey, language)}</span>
            <FaArrowRight className="offer-card-cta-icon" aria-hidden />
          </Link>
        </div>
      );
    }

    return (
      <div className="offer-card-actions">
        <Link
          to={getServiceUrlBySlug(slug)}
          className={`offer-card-cta offer-card-cta-detail${featured ? ' offer-card-cta-featured' : ''}`}
          onClick={onDetailClick}
        >
          <span>{t(singleLabelKey || detailLabelKey, language)}</span>
          <FaArrowRight className="offer-card-cta-icon" aria-hidden />
        </Link>
      </div>
    );
  };

  const renderTrustStack = (keys, variant = 'light') => (
    <div className={`conversion-trust-stack conversion-trust-stack-${variant}`}>
      {keys.map((key) => (
        <span key={key} className="conversion-trust-pill">✓ {t(key, language)}</span>
      ))}
    </div>
  );

  const renderServiceCard = (service) => (
    <Col key={service.id}>
      <div className="landing-service-card h-100">
        <div className="landing-service-icon-wrap" aria-hidden>{service.icon}</div>
        <h3 className="landing-service-title">{t(service.titleKey, language)}</h3>
        <p className="landing-service-desc">{t(service.descKey, language)}</p>
        {isProductDirectCheckout(service.slug) ? (
          renderOfferCardActions({
            slug: service.slug,
            directCheckout: true,
            onPayClick: () => trackLandingEvent('services', `click_${service.id}_pay`),
            onDetailClick: () => trackLandingEvent('services', `click_${service.id}_detail`)
          })
        ) : (
          <Link
            to={getServiceUrlBySlug(service.slug)}
            className="offer-card-cta offer-card-cta-detail landing-service-single-cta"
            onClick={() => trackLandingEvent('services', `click_${service.id}`)}
          >
            <span>{t('landing.productCardCta', language)}</span>
            <FaArrowRight className="offer-card-cta-icon" aria-hidden />
          </Link>
        )}
      </div>
    </Col>
  );

  const waIcon = (
    <svg className="landing-wa-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );

  const renderDomainSearchPanel = () => (
    <div className="landing-domain-embed pb-4 pb-md-5 pt-2" id="dominio">
      <Container className="landing-domain-container">
        <div className="landing-domain-panel domain-search-card landing-domain-panel--prominent rounded-4 p-4 p-lg-5">
          <h2 className="landing-domain-title landing-domain-title--hero mb-2 text-center d-flex align-items-center justify-content-center gap-2 flex-wrap">
            <span aria-hidden>🔍</span>
            <span>{t('landing.domainSearchTitle', language)}</span>
          </h2>
          <p className="domain-search-trust-line text-center mb-3">{t('landing.domainSearchTrustLine', language)}</p>
          <p className="landing-domain-muted text-center landing-domain-lead mb-4 mb-md-5">{t('landing.domainSearchSubtitle', language)}</p>
          <form onSubmit={handleDomainSearch} className="domain-search-form domain-search-form--prominent d-flex flex-column flex-sm-row gap-3 mb-3">
            <input
              type="text"
              className="form-control form-control-lg domain-search-input landing-domain-input"
              autoComplete="off"
              placeholder={t('landing.domainSearchPlaceholder', language)}
              value={domainInput}
              onChange={(ev) => setDomainInput(ev.target.value)}
              aria-label={t('landing.domainSearchPlaceholder', language)}
            />
            <button
              type="submit"
              className="landing-btn-primary px-4 flex-shrink-0 domain-search-submit"
              disabled={domainLoading}
              onClick={() => trackLandingEvent('domain', 'click_search_submit')}
            >
              {domainLoading ? t('landing.domainSearchLoading', language) : t('landing.domainSearchButton', language)}
            </button>
          </form>
          {domainErrorKey && <p className="landing-domain-error small mb-0">{t(domainErrorKey, language)}</p>}
          {domainSuggestions && domainSuggestions.items.length > 0 && (
            <div className="domain-search-result mt-4 pt-4 landing-domain-result-top">
              <p className="fw-semibold mb-2 landing-domain-result-title">{t('landing.domainSuggestionsTitle', language)}</p>
              <p className="small landing-domain-muted mb-3">
                {t('landing.domainSuggestionsFor', language)} <strong>{domainSuggestions.baseLabel}</strong>
              </p>
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-3 landing-domain-table table-dark">
                  <thead>
                    <tr>
                      <th scope="col">{t('landing.domainSuggestionsHeadDomain', language)}</th>
                      <th scope="col">{t('landing.domainSuggestionsHeadStatus', language)}</th>
                      <th scope="col" className="text-end">{t('landing.domainSuggestionsHeadPrice', language)}</th>
                      <th scope="col" className="text-end">{t('landing.domainSuggestionsHeadAction', language)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDomainItems.map((row) => {
                      const selectable = isDomainSelectable(row);
                      const isSelected = selectedDomain === row.domain;
                      return (
                        <tr
                          key={row.domain}
                          role="button"
                          tabIndex={0}
                          className={[
                            isSelected ? 'table-active' : '',
                            !selectable ? 'domain-row-unavailable' : '',
                            selectable ? 'domain-row-available' : ''
                          ].join(' ').trim()}
                          onClick={() => {
                            if (selectable) setSelectedDomain(row.domain);
                          }}
                          onKeyDown={(ev) => {
                            if ((ev.key === 'Enter' || ev.key === ' ') && selectable) {
                              ev.preventDefault();
                              setSelectedDomain(row.domain);
                            }
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="domain-select-checkbox me-2"
                              checked={isSelected}
                              onChange={() => {
                                if (selectable) setSelectedDomain(row.domain);
                              }}
                              disabled={!selectable}
                              aria-label={tf('landing.domainRowSelectLabel', { domain: row.domain }, language)}
                            />
                            {row.domain}
                            {row.premium && (
                              <span className="badge text-bg-warning ms-2">{t('landing.domainPremiumBadge', language)}</span>
                            )}
                            {row.isSpecialOffer && (
                              <span className="domain-special-offer-icon ms-2" aria-label={t('landing.domainSpecialOfferBadge', language)} title={t('landing.domainSpecialOfferBadge', language)}>
                                🔥
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={row.canRegister ? 'text-success' : 'text-secondary'}>
                              {t(domainStatusKey(row.status), language)}
                            </span>
                          </td>
                          <td className="text-end landing-domain-muted small">{formatRegistrationPrice(row, language)}</td>
                          <td className="text-end">
                            {selectable ? (
                              <Link
                                to={getDomainCheckoutHref(row.domain)}
                                className="btn btn-sm landing-btn-primary landing-domain-action-btn"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  trackLandingEvent('domain', 'click_row_buy_now', row.domain);
                                }}
                              >
                                {t('landing.domainRowBuyNow', language)}
                              </Link>
                            ) : (
                              <span className="badge text-bg-warning">{t('landing.domainUnavailableBadge', language)}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <p className="small landing-domain-muted mb-3">{t('landing.domainSuggestionsFootnote', language)}</p>
              <p className="small landing-domain-muted mb-3">{t('landing.domainNextStepNote', language)}</p>
              <div className="d-flex flex-wrap gap-2">
                {selectedDomain ? (
                  <Link
                    to={getDomainCheckoutHref(selectedDomain)}
                    className="landing-btn-primary"
                    onClick={() => trackLandingEvent('domain', 'click_continue_checkout', selectedDomain)}
                  >
                    {t('landing.domainContinueCta', language)}
                  </Link>
                ) : (
                  <button type="button" className="landing-btn-primary" disabled>
                    {t('landing.domainSelectFirst', language)}
                  </button>
                )}
              </div>
              {renderTrustStack([
                'landing.trustDomainOne',
                'landing.trustDomainTwo',
                'landing.trustDomainThree'
              ], 'darkMuted')}
            </div>
          )}
        </div>
      </Container>
    </div>
  );

  const renderCatalogProductCard = (product) => {
    const featured = product.slug === FEATURED_PRODUCT_SLUG || product.slug === ENCAJA_FEATURED_PLAN_SLUG;
    const featuredBadgeKey = product.slug === ENCAJA_FEATURED_PLAN_SLUG
      ? 'landing.encajaPlanPopularBadge'
      : 'landing.offerBusinessBadge';
    const directCheckout = !!product.direct_checkout;
    return (
      <Col key={product.slug} xs={12} md={6} xl={4} className="d-flex">
        <Card className={`offer-card h-100 border-0 shadow-sm w-100 ${featured ? 'offer-card-featured' : ''}`}>
          {featured && (
            <div className="offer-card-tab">{t(featuredBadgeKey, language)}</div>
          )}
          <Card.Body className="p-4 d-flex flex-column text-center h-100">
            <ProductIcon
              iconKey={product.icon_key || 'FaBoxOpen'}
              iconColor={product.icon_color || 'cyan'}
              centered
              className="mb-3"
            />
            <Card.Title className="h5">{product.name}</Card.Title>
            <Card.Text className="text-muted small text-start flex-grow-1 mb-0">{product.description}</Card.Text>
              <div className="offer-card-footer">
              <ProductPriceBlock
                product={product}
                language={language}
                monthlySuffix={getProductPriceSuffix(product)}
              />
              {renderOfferCardActions({
                slug: product.slug,
                directCheckout,
                featured,
                onPayClick: () => trackLandingEvent('offers', `click_product_${product.slug}_pay`),
                onDetailClick: () => trackLandingEvent(
                  'offers',
                  directCheckout ? `click_product_${product.slug}_detail` : `click_product_${product.slug}`
                )
              })}
              </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <div className="landing landing-theme-dark">
      <SiteHeader
        variant="marketing"
        onAuthUserLoaded={setUser}
        onNavTrack={trackLandingEvent}
      />

      {showSection('encajaBanner') && <EncajaStarBanner language={language} />}

      {showSection('hero') && (
      <section className="landing-hero">
        <div className="hero-background" aria-hidden />
        <div className="landing-hero-grid-bg" aria-hidden />
        <Container className="landing-hero-inner">
          <Row className="align-items-center landing-hero-row">
            <Col lg={6} xl={7} className="hero-content">
              <div className="hero-badge mb-3">
                <span className="badge-text">{t('landing.heroBadge', language)}</span>
              </div>
              <h1 className="hero-title">
                <span className="hero-title-line">{t(intentCopy.title, language)}</span>
                <span className="text-gradient-cyan">{t(intentCopy.titleHighlight, language)}</span>
              </h1>
              <p className="lead hero-subtitle mb-4">{t(intentCopy.subtitle, language)}</p>
              {intentCopy.subtitleSupport && (
                <p className="hero-subtitle-support mb-4">{t(intentCopy.subtitleSupport, language)}</p>
              )}
              <div className="hero-proof-strip mb-4">
                <span>{t('landing.heroProofOne', language)}</span>
                <span>{t('landing.heroProofTwo', language)}</span>
                <span>{t('landing.heroProofThree', language)}</span>
              </div>
              <div className="hero-cta-stack">
                <Link
                  to="/user"
                  className="landing-btn-primary hero-primary-cta"
                  onClick={() => trackLandingEvent('hero', 'click_primary_cta')}
                >
                  {t('landing.heroPrimaryCta', language)}
                </Link>
                <a
                  href={whatsappBoliviaHref}
                  target="_blank"
                  rel="noreferrer"
                  className="landing-btn-outline-cyan hero-secondary-btn"
                  onClick={() => trackLandingEvent('hero', 'click_secondary_whatsapp')}
                >
                  {waIcon}
                  {t('landing.heroSecondaryCta', language)}
                </a>
              </div>
            </Col>
            <Col lg={6} xl={5} className="d-none d-lg-block hero-visual-col">
              <div className="hero-dashboard">
                <div className="hero-dashboard-glow" aria-hidden />
                <div className="hero-dashboard-window">
                  <div className="hero-dashboard-chrome">
                    <span className="hero-dashboard-dots" aria-hidden>
                      <i /><i /><i />
                    </span>
                    <span className="hero-dashboard-url">{t('landing.heroMockBrowserUrl', language)}</span>
                  </div>
                  <div className="hero-dashboard-bar" aria-hidden />
                  <div className="hero-dashboard-metrics">
                    <div className="hero-dashboard-metric">
                      <span className="hero-dashboard-metric-label">{t('landing.heroMockMetricVisits', language)}</span>
                      <span className="hero-dashboard-metric-value">{t('landing.heroMockMetricVisitsValue', language)}</span>
                    </div>
                    <div className="hero-dashboard-metric">
                      <span className="hero-dashboard-metric-label">{t('landing.heroMockMetricClients', language)}</span>
                      <span className="hero-dashboard-metric-value">{t('landing.heroMockMetricClientsValue', language)}</span>
                    </div>
                    <div className="hero-dashboard-metric">
                      <span className="hero-dashboard-metric-label">{t('landing.heroMockMetricSpeed', language)}</span>
                      <span className="hero-dashboard-metric-value">{t('landing.heroMockMetricSpeedValue', language)}</span>
                    </div>
                  </div>
                  <div className="hero-dashboard-footer">
                    <span className="hero-dashboard-live-dot" aria-hidden />
                    {t('landing.heroMockLiveStatus', language)}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        {showSection('heroStats') && (
        <div className="landing-stats-strip">
          <Container>
            <div className="landing-stats-row">
              <div className="landing-stat">
                <span className="landing-stat-value">{t('landing.statDeliveredValue', language)}</span>
                <span className="landing-stat-label">{t('landing.statDeliveredLabel', language)}</span>
              </div>
              <div className="landing-stat">
                <span className="landing-stat-value">{t('landing.statReviewsValue', language)}</span>
                <span className="landing-stat-label">{t('landing.statReviewsLabel', language)}</span>
              </div>
              <div className="landing-stat">
                <span className="landing-stat-value">{t('landing.statResponseValue', language)}</span>
                <span className="landing-stat-label">{t('landing.statResponseLabel', language)}</span>
              </div>
              <div className="landing-stat">
                <span className="landing-stat-value">{t('landing.statCoverageValue', language)}</span>
                <span className="landing-stat-label">{t('landing.statCoverageLabel', language)}</span>
              </div>
            </div>
          </Container>
        </div>
        )}
        {showSection('domainSearch') && renderDomainSearchPanel()}
      </section>
      )}

      {!showSection('hero') && showSection('domainSearch') && (
        <section className="landing-domain-standalone py-4 py-md-5">
          {renderDomainSearchPanel()}
        </section>
      )}

      {showSection('whatsappFab') && (
      <a
        href={whatsappBoliviaHref}
        target="_blank"
        rel="noreferrer"
        className="landing-wa-fab"
        aria-label={t('landing.fabWhatsAppAria', language)}
        onClick={() => trackLandingEvent('fab', 'click_whatsapp')}
      >
        {waIcon}
      </a>
      )}

      {showSection('services') && (
      <section id="servicios" className="landing-services py-5">
        <Container>
          <div className="landing-services-intro text-center mb-4 mb-md-5">
            <div className="landing-section-badge mb-3">{t('landing.servicesBadge', language)}</div>
            <h2 className="landing-services-heading mb-3">
              <span className="landing-section-title-base">{t('landing.servicesHeadingLine1', language)}</span>{' '}
              <span className="text-gradient-cyan">{t('landing.servicesHeadingAccent', language)}</span>
            </h2>
            <p className="landing-services-lead mx-auto mb-0">{t('landing.servicesSubtitle', language)}</p>
          </div>
          <Row xs={1} md={3} className="g-4 mb-2">
            {serviceGroups.infra.map((service) => renderServiceCard(service))}
          </Row>
          <Row xs={1} md={3} className="g-4 mb-4">
            {serviceGroups.web.map((service) => renderServiceCard(service))}
          </Row>
          {serviceGroups.extended.length > 0 && (
            <>
              <h3 className="landing-services-more-title text-center mt-5 mb-4">{t('landing.servicesMoreTitle', language)}</h3>
              <Row xs={1} sm={2} lg={4} className="g-4 mb-4">
                {serviceGroups.extended.map((service) => renderServiceCard(service))}
              </Row>
            </>
          )}
          <div className="landing-contact-shell rounded-4 p-4 text-center text-md-start">
            <p className="fw-semibold mb-2 mb-md-3">{t('landing.regionNote', language)}</p>
            <div className="landing-contact-grid mt-3">
              <div className="landing-contact-card">
                <p className="landing-contact-title mb-2">{t('landing.contactSectionTitle', language)}</p>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <a
                    href={whatsappParaguayHref}
                    target="_blank"
                    rel="noreferrer"
                    className="landing-whatsapp-btn"
                    onClick={() => trackLandingEvent('contact', 'click_whatsapp_paraguay')}
                  >
                    <span aria-hidden>🇵🇾</span>
                    <span aria-hidden>🟢</span>
                    <span>{t('landing.contactParaguayLine', language)} {formatLocalPhone(siteContact.whatsappParaguay)}</span>
                  </a>
                  <a
                    href={whatsappBoliviaHref}
                    target="_blank"
                    rel="noreferrer"
                    className="landing-whatsapp-btn"
                    onClick={() => trackLandingEvent('contact', 'click_whatsapp_bolivia')}
                  >
                    <span aria-hidden>🇧🇴</span>
                    <span aria-hidden>🟢</span>
                    <span>{t('landing.contactBoliviaLine', language)} {formatLocalPhone(siteContact.whatsappBolivia)}</span>
                  </a>
                </div>
                <p className="small text-muted mb-2">{supportNote || t('landing.contactEnglishLine', language)}</p>
                {siteContact.email && (
                  <p className="small text-muted mb-0 landing-contact-email">
                    {t('landing.contactEmailLine', language)}{' '}
                    <a href={`mailto:${siteContact.email}`}>{siteContact.email}</a>
                  </p>
                )}
                <div className="lead-form-card mt-3">
                  <p className="fw-semibold mb-2">{t('landing.leadFormTitle', language)}</p>
                  {leadErrorKey && <p className="small text-danger mb-2">{t(leadErrorKey, language)}</p>}
                  {leadStep === 1 ? (
                    <form onSubmit={handleLeadStepOne} className="d-flex flex-column gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t('landing.leadNamePlaceholder', language)}
                        value={leadForm.name}
                        onChange={(e) => setLeadForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                      <Form.Select
                        size="sm"
                        value={leadForm.need}
                        onChange={(e) => setLeadForm((prev) => ({ ...prev, need: e.target.value }))}
                      >
                        <option value="website">{t('landing.leadNeedWebsite', language)}</option>
                        <option value="domain">{t('landing.leadNeedDomain', language)}</option>
                        <option value="software">{t('landing.leadNeedSoftware', language)}</option>
                      </Form.Select>
                      <button type="submit" className="btn btn-sm btn-primary" onClick={() => trackLandingEvent('lead_form', 'click_step1_continue')}>
                        {t('landing.leadStep1Cta', language)}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleLeadSubmit} className="d-flex flex-column gap-2">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        placeholder={t('landing.leadEmailPlaceholder', language)}
                        value={leadForm.email}
                        onChange={(e) => setLeadForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t('landing.leadCompanyPlaceholder', language)}
                        value={leadForm.company}
                        onChange={(e) => setLeadForm((prev) => ({ ...prev, company: e.target.value }))}
                      />
                      <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        placeholder={t('landing.leadNotesPlaceholder', language)}
                        value={leadForm.notes}
                        onChange={(e) => setLeadForm((prev) => ({ ...prev, notes: e.target.value }))}
                      />
                      <div className="d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setLeadStep(1)}>
                          {t('landing.leadBack', language)}
                        </button>
                        <button type="submit" className="btn btn-sm btn-primary">
                          {t('landing.leadSubmitCta', language)}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
              <div className="landing-crypto-card">
                <p className="landing-crypto-title mb-1">₿ {t('landing.cryptoSectionTitle', language)}</p>
                <p className="small mb-2">{t('landing.cryptoSectionBody', language)}</p>
                <p className="small fw-semibold mb-3">{t('landing.cryptoSectionHighlight', language)}</p>
                <a
                  href={whatsappBoliviaHref}
                  target="_blank"
                  rel="noreferrer"
                  className="landing-crypto-cta"
                  onClick={() => trackLandingEvent('contact', 'click_crypto_cta')}
                >
                  {t('landing.cryptoSectionCta', language)}
                </a>
                <div className="landing-crypto-trust mt-3">
                  {renderTrustStack([
                    'landing.trustContactOne',
                    'landing.trustContactTwo',
                    'landing.trustContactThree'
                  ], 'subtle')}
                </div>
              </div>
            </div>
            <p className="text-muted small mb-1">{t('landing.latamClientsLine', language)}</p>
            <p className="fw-semibold small mb-0">{t('landing.noCommitmentLine', language)}</p>
          </div>
        </Container>
      </section>
      )}

      {showSection('offers') && (
      <section id="productos" className="landing-offers landing-offers-section py-5">
        <Container>
          <div className="section-header text-center mb-4 mb-md-5 landing-offers-header">
            <h2 className="fw-bold mb-3 landing-offers-title">{t('landing.offersTitle', language)}</h2>
            <p className="landing-offers-lead mb-0">{t('landing.offersSubtitle', language)}</p>
          </div>
          {saleProducts.length > 0 && (
            <div className="landing-sale-products mb-5">
              <div className="text-center text-md-start mb-3">
                <h3 className="landing-sale-products-title mb-1">{t('landing.offersDiscountTitle', language)}</h3>
                <p className="landing-sale-products-lead mb-0">{t('landing.offersDiscountLead', language)}</p>
              </div>
              <div className="landing-sale-products-grid">
                {saleProducts.map((product) => (
                  <SaleProductCard
                    key={product.id}
                    product={product}
                    language={language}
                    productHref={getServiceUrlBySlug(product.slug)}
                    checkoutHref={getProductCheckoutHref(product.slug)}
                    showPayNow={!!product.direct_checkout}
                    onPayClick={() => trackLandingEvent('offers', `click_sale_${product.slug}_pay`)}
                    onViewClick={() => trackLandingEvent('offers', `click_sale_${product.slug}_detail`)}
                  />
                ))}
              </div>
            </div>
          )}
          {catalogGroupsWithoutSaleDuplicates.length > 0 ? (
            catalogGroupsWithoutSaleDuplicates.map((group) => (
              <div
                key={group.slug}
                id={group.slug === 'encaja' ? 'productos-encaja' : undefined}
                className="landing-catalog-group mb-5"
              >
                <h3 className="h5 text-center text-md-start mb-4 landing-catalog-group-title">{group.name}</h3>
                <Row xs={1} md={2} xl={3} className="g-4">
                  {group.products.map((product) => renderCatalogProductCard(product))}
                </Row>
              </div>
            ))
          ) : (
            <Row xs={1} md={2} xl={4} className="g-4">
              {offerLadder.map((tier) => (
                <Col key={tier.id}>
                  <Card className={`offer-card h-100 border-0 shadow-sm ${tier.featured ? 'offer-card-featured' : ''}`}>
                    {tier.featured && (
                      <div className="offer-card-tab">{t(tier.badgeKey, language)}</div>
                    )}
                    <Card.Body className="p-4 d-flex flex-column h-100">
                      <Card.Title className="h5">{t(tier.titleKey, language)}</Card.Title>
                      <Card.Text className="text-muted small flex-grow-1 mb-0">{t(tier.descKey, language)}</Card.Text>
                      <p className="small mb-1"><strong>{t('landing.offerBestForLabel', language)}:</strong> {t(tier.bestForKey, language)}</p>
                      <p className="small text-muted mb-0"><strong>{t('landing.offerUpgradeLabel', language)}:</strong> {t(tier.upgradeToKey, language)}</p>
                      <div className="offer-card-footer">
                        <div className="offer-price">{getOfferPriceLabel(tier.slug, tier.priceKey)}</div>
                        {renderOfferCardActions({
                          slug: tier.slug,
                          directCheckout: isProductDirectCheckout(tier.slug),
                          featured: tier.featured,
                          payLabelKey: 'landing.offerStarterCta',
                          detailLabelKey: 'landing.offerViewDetailCta',
                          singleLabelKey: tier.ctaKey,
                          onPayClick: () => trackLandingEvent('offers', `click_offer_${tier.id}_pay`),
                          onDetailClick: () => trackLandingEvent(
                            'offers',
                            isProductDirectCheckout(tier.slug) ? `click_offer_${tier.id}_detail` : `click_offer_${tier.id}`
                          )
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          <div className="mt-3">
            {renderTrustStack([
              'landing.trustOffersOne',
              'landing.trustOffersTwo',
              'landing.trustOffersThree'
            ], 'darkMuted')}
          </div>
          <div className="mt-3">
            {showSection('conversionProof') && (
              <ConversionProofBlock language={language} context="landing_offers" />
            )}
          </div>
        </Container>
      </section>
      )}

      {showSection('process') && (
      <section id="proceso" className="landing-process py-5">
        <Container>
          <Row className="align-items-start g-4 g-xl-5">
            <Col lg={5} xl={5}>
              <div className="landing-section-badge landing-process-badge mb-3">{t('landing.processBadge', language)}</div>
              <h2 className="landing-process-title mb-3">
                <span className="landing-process-title-line">{t('landing.processHeadingLead', language)}</span>{' '}
                <span className="landing-text-peach">{t('landing.processHeadingAccent', language)}</span>{' '}
                <span className="landing-process-title-line">{t('landing.processHeadingTrail', language)}</span>
              </h2>
              <p className="landing-process-subtitle mb-0">{t('landing.processSubtitle', language)}</p>
            </Col>
            <Col lg={7} xl={7}>
              <Row xs={1} sm={2} className="g-3">
                {processSteps.map((step) => (
                  <Col key={step.label}>
                    <div className="landing-process-card h-100">
                      <span className="landing-process-num">{step.label}</span>
                      <h3 className="landing-process-card-title">{t(step.titleKey, language)}</h3>
                      <p className="landing-process-card-desc mb-0">{t(step.descKey, language)}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>
      )}

      {showSection('whyChoose') && (
      <section id="por-que" className="landing-why py-5">
        <Container>
          <div className="text-center mb-4 mb-md-5">
            <div className="landing-section-badge mb-3">{t('landing.whyChooseBadge', language)}</div>
            <h2 className="landing-why-heading mb-0">
              <span className="landing-why-heading-line d-block">
                {t('landing.whyChooseLine1a', language)}{' '}
                <span className="text-gradient-cyan">{t('landing.whyChooseLine1b', language)}</span>
              </span>
              <span className="landing-why-heading-line d-block mt-1">
                {t('landing.whyChooseLine2a', language)}{' '}
                <span className="text-gradient-cyan">{t('landing.whyChooseLine2b', language)}</span>
              </span>
            </h2>
          </div>
          <Row xs={1} sm={2} lg={3} className="g-4">
            {whyChooseItems.map((item) => (
              <Col key={item.titleKey}>
                <div className="landing-why-card h-100">
                  <div className="landing-why-icon-wrap" aria-hidden>{item.icon}</div>
                  <h3 className="landing-why-card-title">{t(item.titleKey, language)}</h3>
                  <p className="landing-why-card-desc mb-0">{t(item.descKey, language)}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
      )}

      {showSection('testimonials') && clientTestimonials.length > 0 && (
      <section id="clientes" className="landing-testimonials py-5">
        <Container>
          <div className="text-center mb-4 mb-md-5">
            <div className="landing-section-badge mb-3">{t('landing.clientsBadge', language)}</div>
            <h2 className="landing-section-title mb-0">
              <span className="landing-section-title-base">{t('landing.clientsHeadingLead', language)}</span>{' '}
              <span className="text-gradient-cyan">{t('landing.clientsHeadingAccent', language)}</span>
            </h2>
          </div>
          <Row xs={1} md={3} className="g-4">
            {clientTestimonials.map((item) => {
              const roleLine = getTestimonialLocalizedField(item, 'role_line');
              const flag = countryCodeToFlagEmoji(item.country_code);
              return (
              <Col key={item.id}>
                <article className="landing-testimonial-card h-100">
                  <div className="landing-testimonial-stars" aria-hidden>{renderTestimonialStars(item.stars)}</div>
                  <blockquote className="landing-testimonial-quote">{getTestimonialLocalizedField(item, 'quote')}</blockquote>
                  <div className="landing-testimonial-footer">
                    <span className="landing-testimonial-avatar">{item.avatar_initial || item.person_name?.charAt(0)}</span>
                    <div>
                      <p className="landing-testimonial-name mb-0">{item.person_name}</p>
                      <p className="landing-testimonial-meta mb-0">
                        {roleLine}
                        {roleLine && flag ? ' · ' : ''}
                        {flag ? (
                          <span className="landing-testimonial-flag" aria-hidden>{flag}</span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                </article>
              </Col>
              );
            })}
          </Row>
        </Container>
      </section>
      )}

      {showSection('news') && latestPosts.length > 0 && (
        <section id="noticias" className="landing-news landing-news-section py-4 py-md-5">
          <Container>
            <div className="landing-news-header d-flex flex-wrap align-items-center justify-content-center justify-content-md-between gap-3 mb-4">
              <h2 className="landing-news-heading text-center text-md-start mb-0">{t('landing.latestNewsTitle', language)}</h2>
              <Link to="/all-news" className="landing-news-all-link">
                {t('landing.allNewsLink', language)}
              </Link>
            </div>
            <Carousel indicators controls className="landing-news-carousel">
              {latestPosts.map((post) => (
                <Carousel.Item key={post.id}>
                  <Link to={getPostUrl(post)} className="landing-news-slide-link text-decoration-none d-block">
                    <div className="landing-news-slide-card row align-items-stretch g-0 overflow-hidden rounded-4">
                      <div className="col-md-5 landing-news-slide-media">
                        {post.featured_image ? (
                          <img
                            src={
                              post.featured_image.startsWith('http')
                                ? post.featured_image
                                : (process.env.REACT_APP_API_URL || '') + post.featured_image
                            }
                            alt=""
                            className="w-100 h-100 object-fit-cover"
                            style={{ minHeight: 220, maxHeight: 280, objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="landing-news-slide-placeholder d-flex align-items-center justify-content-center" style={{ minHeight: 220 }}>
                            <span className="landing-news-slide-placeholder-icon" aria-hidden>
                              📰
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="col-md-7 landing-news-slide-body p-4">
                        <h3 className="landing-news-slide-title mb-2">{post.title}</h3>
                        <p className="landing-news-slide-excerpt small mb-0 line-clamp-3">{post.excerpt || post.title}</p>
                        <span className="landing-news-slide-cta small mt-3 d-inline-block">{t('landing.latestNewsReadMore', language)} →</span>
                      </div>
                    </div>
                  </Link>
                </Carousel.Item>
              ))}
            </Carousel>
          </Container>
        </section>
      )}

      {showSection('news') && !loadingNews && latestPosts.length === 0 && (
        <section id="noticias" className="landing-news landing-news-section py-4 py-md-5">
          <Container>
            <div className="landing-news-header d-flex flex-wrap align-items-center justify-content-center justify-content-md-between gap-3 mb-4">
              <h2 className="landing-news-heading text-center text-md-start mb-0">{t('landing.latestNewsTitle', language)}</h2>
              <Link to="/all-news" className="landing-news-all-link">
                {t('landing.allNewsLink', language)}
              </Link>
            </div>
            <p className="landing-news-empty text-center mb-0">{t('landing.latestNewsNone', language)}</p>
          </Container>
        </section>
      )}

      {showSection('faq') && (
      <section id="faq" className="landing-faq py-5">
        <Container>
          <div className="landing-section-badge text-center mb-3">{t('landing.faqBadge', language)}</div>
          <h2 className="landing-section-title text-center mb-4">
            <span className="landing-section-title-base">{t('landing.faqHeadingLead', language)}</span>{' '}
            <span className="text-gradient-cyan">{t('landing.faqHeadingAccent', language)}</span>
          </h2>
          <div className="landing-faq-panel">
            {faqItems.map((item, idx) => {
              const n = idx + 1;
              const open = faqOpen === n;
              return (
                <div key={item.q} className={`landing-faq-item ${open ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="landing-faq-q"
                    aria-expanded={open}
                    onClick={() => {
                      setFaqOpen(open ? null : n);
                      trackLandingEvent('faq', open ? 'close' : 'open', String(n));
                    }}
                  >
                    <span>{t(item.q, language)}</span>
                    <span className="landing-faq-chevron" aria-hidden />
                  </button>
                  {open && (
                    <div className="landing-faq-a">{t(item.a, language)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </Container>
      </section>
      )}

      {showSection('finalCta') && (
      <section id="cta-final" className="landing-final-cta py-5">
        <Container>
          <div className="landing-final-cta-card">
            <div className="landing-final-cta-glow" aria-hidden />
            <h2 className="landing-final-cta-title mb-3">
              <span className="landing-final-cta-line d-block">{t('landing.finalCtaLine1', language)}</span>
              <span className="text-gradient-cyan d-block">{t('landing.finalCtaLine2', language)}</span>
            </h2>
            <p className="landing-final-cta-subtitle mb-4">{t('landing.finalCtaSubtitle', language)}</p>
            <div className="landing-final-cta-actions">
              <a
                href={whatsappBoliviaHref}
                target="_blank"
                rel="noreferrer"
                className="landing-btn-primary landing-final-cta-primary"
                onClick={() => trackLandingEvent('final_cta', 'click_whatsapp')}
              >
                {waIcon}
                <span>{t('landing.finalCtaPrimary', language)}</span>
              </a>
              {siteContact.email ? (
                <a
                  href={`mailto:${siteContact.email}`}
                  className="landing-final-cta-secondary"
                  onClick={() => trackLandingEvent('final_cta', 'click_email')}
                >
                  {t('landing.finalCtaSecondary', language)}
                </a>
              ) : null}
            </div>
          </div>
        </Container>
      </section>
      )}

      {showSection('mobileStickyCta') && (
      <div className="mobile-sticky-cta d-md-none landing-mobile-sticky">
        <a
          href={whatsappBoliviaHref}
          target="_blank"
          rel="noreferrer"
          className="landing-btn-primary mobile-sticky-cta-primary"
          onClick={() => trackLandingEvent('mobile_sticky', 'click_whatsapp')}
        >
          {waIcon}
          <span>{t('landing.mobileStickyPrimaryCta', language)}</span>
        </a>
        {siteContact.email ? (
          <a
            href={`mailto:${siteContact.email}`}
            className="landing-mobile-sticky-mail"
            onClick={() => trackLandingEvent('mobile_sticky', 'click_email')}
          >
            {t('landing.mobileStickySecondaryCta', language)}
          </a>
        ) : user ? (
          <Link
            to={['admin', 'manager', 'sales'].includes(user.role) ? '/app/dashboard' : '/user'}
            className="landing-mobile-sticky-account"
            onClick={() => trackLandingEvent('mobile_sticky', 'click_account')}
          >
            {t('nav.account', language)}
          </Link>
        ) : (
          <Link
            to="/user"
            className="landing-mobile-sticky-login"
            onClick={() => trackLandingEvent('mobile_sticky', 'click_login')}
          >
            {t('nav.login', language)}
          </Link>
        )}
      </div>
      )}

      {showSection('footer') && (
      <footer className="landing-footer-v2 py-4">
        <Container>
          <div className="landing-footer-grid">
            <Link
              to="/"
              className="landing-footer-brand text-decoration-none"
              onClick={() => trackLandingEvent('footer', 'click_logo')}
            >
              <img src={BRAND_LOGO_MONO} alt="" className="landing-nav-logo-icon landing-footer-logo-icon" width={36} height={36} aria-hidden />
              {language === 'en' ? (
                <span className="landing-footer-brand-text landing-brand-full">{t('landing.footerBrandFull', language)}</span>
              ) : (
                <span className="landing-footer-brand-text">
                  <span className="landing-footer-part-estu">{t('landing.footerWordEstu', language)}</span>{' '}
                  <span className="landing-footer-part-mejor">{t('landing.footerWordMejor', language)}</span>{' '}
                  <span className="landing-footer-part-opcion">{t('landing.footerWordOpcion', language)}</span>
                </span>
              )}
            </Link>
            <nav className="landing-footer-nav" aria-label="Footer">
              {visibleMarketingNav.map((item) => (
                <a key={item.id} href={`#${item.id}`} className="landing-footer-link">
                  {t(item.labelKey, language)}
                </a>
              ))}
              <a
                href={whatsappBoliviaHref}
                target="_blank"
                rel="noreferrer"
                className="landing-footer-link"
                onClick={() => trackLandingEvent('footer', 'click_whatsapp')}
              >
                {t('landing.navWhatsAppCta', language)}
              </a>
              <a
                href={ENCAJA_PLANS_ANCHOR}
                className="landing-footer-link"
                onClick={() => trackLandingEvent('footer', 'click_encaja_plans')}
              >
                {t('landing.footerEncajaPlans', language)}
              </a>
              <a
                href={ENCAJA_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-footer-link"
                onClick={() => trackLandingEvent('footer', 'click_encaja_app')}
              >
                {t('landing.footerEncajaPanel', language)}
              </a>
            </nav>
            <p className="landing-footer-copy mb-0">{t('landing.footerCopyrightLine', language)}</p>
          </div>
        </Container>
      </footer>
      )}
    </div>
  );
};

export default Landing;
